import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Paper,
  Typography,
  Box,
  AppBar,
  Toolbar,
  Button,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Alert,
  Snackbar,
  Chip
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  AdminPanelSettings as AdminIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Check as CheckIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import axios from 'axios';

const PROVINCE_VALUES = [
  'bresil',
  'region_asie',
  'madagascar',
  'europe_togo',
  'amerique_nord',
  'nouvelle_zelande',
  'generalat'
];

function Admin({ onLogout }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [users, setUsers] = useState([]);
  const [sites, setSites] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [pendingValidations, setPendingValidations] = useState([]);
  const [openUserDialog, setOpenUserDialog] = useState(false);
  const [openPermissionDialog, setOpenPermissionDialog] = useState(false);
  const [openValidationDialog, setOpenValidationDialog] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [currentPermission, setCurrentPermission] = useState(null);
  const [validationAction, setValidationAction] = useState({ id: null, status: '' });
  const [validationComment, setValidationComment] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [isAdmin, setIsAdmin] = useState(false);
  const [loggedInUser, setLoggedInUser] = useState(null);

  const [userForm, setUserForm] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    language: 'fr',
    role: 'user',
    province: ''
  });

  const [permissionForm, setPermissionForm] = useState({
    user_id: '',
    site_id: '',
    can_view: true,
    can_edit: false,
    can_delete: false
  });

  useEffect(() => {
    checkAdminStatus();
  }, []);

  useEffect(() => {
    if (isAdmin) {
      fetchUsers();
      fetchSites();
      fetchPermissions();
      fetchPendingValidations();
    }
  }, [isAdmin]);

  const checkAdminStatus = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user'));

      if (!user || (user.role !== 'super_admin' && user.role !== 'admin_province')) {
        showSnackbar(t('accessDeniedAdmin'), 'error');
        navigate('/');
        return;
      }

      setLoggedInUser(user);
      setIsAdmin(true);
    } catch (error) {
      console.error(error);
      showSnackbar(t('errorCheckingAdmin'), 'error');
      navigate('/');
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error(error);
      showSnackbar(t('errorFetchingUsers'), 'error');
    }
  };

  const fetchSites = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/sites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSites(response.data);
    } catch (error) {
      console.error(error);
      showSnackbar(t('errorFetchingSites'), 'error');
    }
  };

  const fetchPermissions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin/permissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPermissions(response.data);
    } catch (error) {
      console.error(error);
      showSnackbar(t('errorFetchingPermissions'), 'error');
    }
  };

  const fetchPendingValidations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin/pending-validations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingValidations(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const handleOpenUserDialog = (user = null) => {
    const isAdminProvince = loggedInUser && loggedInUser.role === 'admin_province';

    if (user) {
      setCurrentUser(user);
      setUserForm({
        email: user.email,
        password: '',
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        language: user.language || 'fr',
        role: isAdminProvince ? (user.role === 'super_admin' ? 'user' : (user.role || 'user')) : (user.role || 'user'),
        province: isAdminProvince ? (loggedInUser.province || '') : (user.province || '')
      });
    } else {
      setCurrentUser(null);
      setUserForm({
        email: '',
        password: '',
        first_name: '',
        last_name: '',
        language: 'fr',
        role: isAdminProvince ? 'user' : 'user',
        province: isAdminProvince ? (loggedInUser.province || '') : ''
      });
    }
    setOpenUserDialog(true);
  };

  const handleCloseUserDialog = () => {
    setOpenUserDialog(false);
    setCurrentUser(null);
  };

  const handleSaveUser = async () => {
    try {
      const token = localStorage.getItem('token');

      if (currentUser) {
        // Update user
        await axios.put(
          `${process.env.REACT_APP_API_URL}/api/admin/users/${currentUser.id}`,
          userForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        showSnackbar(t('userUpdatedSuccess'), 'success');
      } else {
        // Create user
        if (!userForm.password) {
          showSnackbar(t('passwordRequired'), 'error');
          return;
        }
        const pwd = userForm.password;
        const passwordValid = pwd.length >= 12 && /[a-z]/.test(pwd) && /[A-Z]/.test(pwd) && /[0-9]/.test(pwd) && /[@$!%*?&#]/.test(pwd);
        if (!passwordValid) {
          const failing = [
            { test: pwd.length >= 12, msg: t('passwordReqMinLength') },
            { test: /[a-z]/.test(pwd), msg: t('passwordReqLowercase') },
            { test: /[A-Z]/.test(pwd), msg: t('passwordReqUppercase') },
            { test: /[0-9]/.test(pwd), msg: t('passwordReqDigit') },
            { test: /[@$!%*?&#]/.test(pwd), msg: t('passwordReqSpecial') },
          ].filter(r => !r.test).map(r => r.msg);
          showSnackbar(failing.join('\n'), 'error');
          return;
        }
        await axios.post(
          `${process.env.REACT_APP_API_URL}/api/admin/users`,
          userForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        showSnackbar(t('userCreatedSuccess'), 'success');
      }

      handleCloseUserDialog();
      fetchUsers();
    } catch (error) {
      console.error(error);
      if (error.response?.data?.errors) {
        const messages = error.response.data.errors.map(e => e.msg).join('\n');
        showSnackbar(messages, 'error');
      } else {
        showSnackbar(error.response?.data?.error || t('errorSavingUser'), 'error');
      }
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm(t('confirmDeleteUser'))) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/admin/users/${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSnackbar(t('userDeletedSuccess'), 'success');
      fetchUsers();
    } catch (error) {
      console.error(error);
      showSnackbar(error.response?.data?.error || t('errorDeletingUser'), 'error');
    }
  };

  const handleOpenPermissionDialog = (permission = null) => {
    if (permission) {
      setCurrentPermission(permission);
      setPermissionForm({
        user_id: permission.user_id,
        site_id: permission.site_id,
        can_view: permission.can_view || false,
        can_edit: permission.can_edit || false,
        can_delete: permission.can_delete || false
      });
    } else {
      setCurrentPermission(null);
      setPermissionForm({
        user_id: '',
        site_id: '',
        can_view: true,
        can_edit: false,
        can_delete: false
      });
    }
    setOpenPermissionDialog(true);
  };

  const handleClosePermissionDialog = () => {
    setOpenPermissionDialog(false);
    setCurrentPermission(null);
  };

  const handleSavePermission = async () => {
    try {
      const token = localStorage.getItem('token');

      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/admin/permissions`,
        permissionForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showSnackbar(t('permissionSavedSuccess'), 'success');
      handleClosePermissionDialog();
      fetchPermissions();
    } catch (error) {
      console.error(error);
      showSnackbar(error.response?.data?.error || t('errorSavingPermission'), 'error');
    }
  };

  const handleDeletePermission = async (permissionId) => {
    if (!window.confirm(t('confirmDeletePermission'))) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/admin/permissions/${permissionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSnackbar(t('permissionDeletedSuccess'), 'success');
      fetchPermissions();
    } catch (error) {
      console.error(error);
      showSnackbar(error.response?.data?.error || t('errorDeletingPermission'), 'error');
    }
  };

  const handleOpenValidationDialog = (id, status) => {
    setValidationAction({ id, status });
    setValidationComment('');
    setOpenValidationDialog(true);
  };

  const handleCloseValidationDialog = () => {
    setOpenValidationDialog(false);
    setValidationAction({ id: null, status: '' });
    setValidationComment('');
  };

  const handleSubmitValidation = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/risk-analyses/${validationAction.id}/validate`,
        {
          validation_status: validationAction.status,
          validation_comment: validationComment
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (validationAction.status === 'valide') {
        showSnackbar(t('investmentValidated'), 'success');
      } else {
        showSnackbar(t('investmentRejected'), 'success');
      }

      handleCloseValidationDialog();
      fetchPendingValidations();
    } catch (error) {
      console.error(error);
      showSnackbar(error.response?.data?.error || t('errorSavingPermission'), 'error');
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user ? `${user.first_name} ${user.last_name} (${user.email})` : userId;
  };

  const getSiteName = (siteId) => {
    const site = sites.find(s => s.id === siteId);
    return site ? site.nom_site : siteId;
  };

  const getRoleChipColor = (role) => {
    switch (role) {
      case 'super_admin':
        return 'error';
      case 'admin_province':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'super_admin':
        return t('roles.super_admin');
      case 'admin_province':
        return t('roles.admin_province');
      case 'user':
        return t('roles.user');
      default:
        return role || t('roles.user');
    }
  };

  const isLoggedInAdminProvince = loggedInUser && loggedInUser.role === 'admin_province';

  if (!isAdmin) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar position="static">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate('/')}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <AdminIcon sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            {t('administration')}
          </Typography>
          <Button color="inherit" onClick={onLogout}>
            {t('logout')}
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ mt: 4, mb: 4, flexGrow: 1 }}>
        {/* Tableau récapitulatif des droits par rôle */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
            {t('rolesDescription.title')}
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>{t('rolesDescription.permission')}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>
                    <Chip label={t('roles.super_admin')} color="error" size="small" />
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>
                    <Chip label={t('roles.admin_province')} color="warning" size="small" />
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>
                    <Chip label={t('roles.user')} size="small" />
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>{t('rolesDescription.access_all_sites')}</TableCell>
                  <TableCell align="center"><CheckIcon color="success" fontSize="small" /></TableCell>
                  <TableCell align="center"><CloseIcon color="error" fontSize="small" /></TableCell>
                  <TableCell align="center"><CloseIcon color="error" fontSize="small" /></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>{t('rolesDescription.access_province_sites')}</TableCell>
                  <TableCell align="center"><CheckIcon color="success" fontSize="small" /></TableCell>
                  <TableCell align="center"><CheckIcon color="success" fontSize="small" /></TableCell>
                  <TableCell align="center"><CloseIcon color="error" fontSize="small" /></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>{t('rolesDescription.create_sites')}</TableCell>
                  <TableCell align="center"><Typography variant="caption" color="text.secondary">{t('rolesDescription.all_provinces')}</Typography></TableCell>
                  <TableCell align="center"><Typography variant="caption" color="text.secondary">{t('rolesDescription.own_province_only')}</Typography></TableCell>
                  <TableCell align="center"><Typography variant="caption" color="text.secondary">{t('rolesDescription.assigned_sites_only')}</Typography></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>{t('rolesDescription.manage_users')}</TableCell>
                  <TableCell align="center"><CheckIcon color="success" fontSize="small" /></TableCell>
                  <TableCell align="center"><CheckIcon color="success" fontSize="small" /></TableCell>
                  <TableCell align="center"><CloseIcon color="error" fontSize="small" /></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>{t('rolesDescription.manage_permissions')}</TableCell>
                  <TableCell align="center"><CheckIcon color="success" fontSize="small" /></TableCell>
                  <TableCell align="center"><CheckIcon color="success" fontSize="small" /></TableCell>
                  <TableCell align="center"><CloseIcon color="error" fontSize="small" /></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>{t('rolesDescription.validate_investments')}</TableCell>
                  <TableCell align="center"><CheckIcon color="success" fontSize="small" /></TableCell>
                  <TableCell align="center"><CheckIcon color="success" fontSize="small" /></TableCell>
                  <TableCell align="center"><CloseIcon color="error" fontSize="small" /></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        <Paper sx={{ p: 3 }}>
          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
            <Tab label={t('users')} />
            <Tab label={t('permissions')} />
            <Tab label={t('validationTab')} />
          </Tabs>

          {/* Users Tab */}
          {tabValue === 0 && (
            <Box sx={{ mt: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">{t('userManagement')}</Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenUserDialog()}
                >
                  {t('addUser')}
                </Button>
              </Box>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('email')}</TableCell>
                      <TableCell>{t('firstName')}</TableCell>
                      <TableCell>{t('lastName')}</TableCell>
                      <TableCell>{t('language')}</TableCell>
                      <TableCell>{t('role')}</TableCell>
                      <TableCell>{t('province')}</TableCell>
                      <TableCell align="right">{t('actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>{user.first_name}</TableCell>
                        <TableCell>{user.last_name}</TableCell>
                        <TableCell>{user.language}</TableCell>
                        <TableCell>
                          <Chip
                            label={getRoleLabel(user.role)}
                            color={getRoleChipColor(user.role)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {user.province ? t(`provinces.${user.province}`) : '-'}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton onClick={() => handleOpenUserDialog(user)}>
                            <EditIcon />
                          </IconButton>
                          <IconButton onClick={() => handleDeleteUser(user.id)}>
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Permissions Tab */}
          {tabValue === 1 && (
            <Box sx={{ mt: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">{t('permissionManagement')}</Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenPermissionDialog()}
                >
                  {t('addPermission')}
                </Button>
              </Box>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('user')}</TableCell>
                      <TableCell>{t('site')}</TableCell>
                      <TableCell>{t('canView')}</TableCell>
                      <TableCell>{t('canEdit')}</TableCell>
                      <TableCell>{t('canDelete')}</TableCell>
                      <TableCell align="right">{t('actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {permissions.map((permission) => (
                      <TableRow key={permission.id}>
                        <TableCell>{getUserName(permission.user_id)}</TableCell>
                        <TableCell>{getSiteName(permission.site_id)}</TableCell>
                        <TableCell>{permission.can_view ? t('yes') : t('no')}</TableCell>
                        <TableCell>{permission.can_edit ? t('yes') : t('no')}</TableCell>
                        <TableCell>{permission.can_delete ? t('yes') : t('no')}</TableCell>
                        <TableCell align="right">
                          <IconButton onClick={() => handleOpenPermissionDialog(permission)}>
                            <EditIcon />
                          </IconButton>
                          <IconButton onClick={() => handleDeletePermission(permission.id)}>
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Validations Tab */}
          {tabValue === 2 && (
            <Box sx={{ mt: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">{t('pendingValidations')}</Typography>
              </Box>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('site')}</TableCell>
                      <TableCell>{t('theme')}</TableCell>
                      <TableCell>{t('item')}</TableCell>
                      <TableCell>{t('score')}</TableCell>
                      <TableCell>{t('amount')}</TableCell>
                      <TableCell>{t('user')}</TableCell>
                      <TableCell align="right">{t('actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pendingValidations.map((validation) => (
                      <TableRow key={validation.id}>
                        <TableCell>{validation.nom_site || validation.site_id}</TableCell>
                        <TableCell>{validation.theme}</TableCell>
                        <TableCell>{validation.item}</TableCell>
                        <TableCell>{validation.score}</TableCell>
                        <TableCell>{validation.montant_investissement}</TableCell>
                        <TableCell>{validation.creator_name || validation.created_by}</TableCell>
                        <TableCell align="right">
                          <IconButton
                            onClick={() => handleOpenValidationDialog(validation.id, 'valide')}
                            sx={{ color: 'success.main' }}
                          >
                            <CheckCircleIcon />
                          </IconButton>
                          <IconButton
                            onClick={() => handleOpenValidationDialog(validation.id, 'rejete')}
                            sx={{ color: 'error.main' }}
                          >
                            <CancelIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    {pendingValidations.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                            {t('pendingValidations')} : 0
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Paper>
      </Container>

      {/* User Dialog */}
      <Dialog open={openUserDialog} onClose={handleCloseUserDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{currentUser ? t('editUser') : t('addUser')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              fullWidth
              label={t('email')}
              type="email"
              value={userForm.email}
              onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
              required
            />
            <TextField
              fullWidth
              label={t('password')}
              type="password"
              value={userForm.password}
              onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
              helperText={currentUser ? t('leaveEmptyToKeepCurrent') : t('required')}
              required={!currentUser}
            />
            {(!currentUser || userForm.password) && userForm.password !== undefined && (
              <Box sx={{ pl: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                  {t('passwordRequirements')}
                </Typography>
                {[
                  { key: 'passwordReqMinLength', test: userForm.password?.length >= 12 },
                  { key: 'passwordReqLowercase', test: /[a-z]/.test(userForm.password || '') },
                  { key: 'passwordReqUppercase', test: /[A-Z]/.test(userForm.password || '') },
                  { key: 'passwordReqDigit', test: /[0-9]/.test(userForm.password || '') },
                  { key: 'passwordReqSpecial', test: /[@$!%*?&#]/.test(userForm.password || '') },
                ].map(({ key, test }) => (
                  <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {test ? (
                      <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
                    ) : (
                      <CloseIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                    )}
                    <Typography
                      variant="caption"
                      sx={{ color: test ? 'success.main' : 'text.secondary' }}
                    >
                      {t(key)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
            <TextField
              fullWidth
              label={t('firstName')}
              value={userForm.first_name}
              onChange={(e) => setUserForm({ ...userForm, first_name: e.target.value })}
            />
            <TextField
              fullWidth
              label={t('lastName')}
              value={userForm.last_name}
              onChange={(e) => setUserForm({ ...userForm, last_name: e.target.value })}
            />
            <TextField
              fullWidth
              select
              label={t('language')}
              value={userForm.language}
              onChange={(e) => setUserForm({ ...userForm, language: e.target.value })}
            >
              <MenuItem value="fr">Français</MenuItem>
              <MenuItem value="en">English</MenuItem>
              <MenuItem value="pt">Português</MenuItem>
              <MenuItem value="mg">Malagasy</MenuItem>
            </TextField>
            <TextField
              fullWidth
              select
              label={t('role')}
              value={userForm.role}
              onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
            >
              {!isLoggedInAdminProvince && (
                <MenuItem value="super_admin">{t('roles.super_admin')}</MenuItem>
              )}
              <MenuItem value="admin_province">{t('roles.admin_province')}</MenuItem>
              <MenuItem value="user">{t('roles.user')}</MenuItem>
            </TextField>
            <TextField
              fullWidth
              select
              label={t('province')}
              value={userForm.province}
              onChange={(e) => setUserForm({ ...userForm, province: e.target.value })}
              disabled={isLoggedInAdminProvince}
            >
              <MenuItem value="">-</MenuItem>
              {PROVINCE_VALUES.map((prov) => (
                <MenuItem key={prov} value={prov}>
                  {t(`provinces.${prov}`)}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUserDialog}>{t('cancel')}</Button>
          <Button onClick={handleSaveUser} variant="contained">
            {t('save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Permission Dialog */}
      <Dialog open={openPermissionDialog} onClose={handleClosePermissionDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{currentPermission ? t('editPermission') : t('addPermission')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              fullWidth
              select
              label={t('user')}
              value={permissionForm.user_id}
              onChange={(e) => setPermissionForm({ ...permissionForm, user_id: e.target.value })}
              disabled={!!currentPermission}
              required
            >
              {users.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  {user.first_name} {user.last_name} ({user.email})
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              select
              label={t('site')}
              value={permissionForm.site_id}
              onChange={(e) => setPermissionForm({ ...permissionForm, site_id: e.target.value })}
              disabled={!!currentPermission}
              required
            >
              {sites.map((site) => (
                <MenuItem key={site.id} value={site.id}>
                  {site.nom_site}
                </MenuItem>
              ))}
            </TextField>
            <FormControlLabel
              control={
                <Checkbox
                  checked={permissionForm.can_view}
                  onChange={(e) => setPermissionForm({ ...permissionForm, can_view: e.target.checked })}
                />
              }
              label={t('canView')}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={permissionForm.can_edit}
                  onChange={(e) => setPermissionForm({ ...permissionForm, can_edit: e.target.checked })}
                />
              }
              label={t('canEdit')}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={permissionForm.can_delete}
                  onChange={(e) => setPermissionForm({ ...permissionForm, can_delete: e.target.checked })}
                />
              }
              label={t('canDelete')}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePermissionDialog}>{t('cancel')}</Button>
          <Button onClick={handleSavePermission} variant="contained">
            {t('save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Validation Dialog */}
      <Dialog open={openValidationDialog} onClose={handleCloseValidationDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {validationAction.status === 'valide' ? t('validate') : t('reject')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label={t('validationComment')}
              value={validationComment}
              onChange={(e) => setValidationComment(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseValidationDialog}>{t('cancel')}</Button>
          <Button
            onClick={handleSubmitValidation}
            variant="contained"
            color={validationAction.status === 'valide' ? 'success' : 'error'}
          >
            {validationAction.status === 'valide' ? t('validate') : t('reject')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%', whiteSpace: 'pre-line' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Admin;
